/**
 * BreadcrumbContext
 *
 * Allows pages to provide label overrides for URL path segments
 * in breadcrumbs (e.g., replacing a UUID with a readable case number).
 */

import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface BreadcrumbContextValue {
  /** Map of path segment → display label overrides */
  overrides: Record<string, string>;
  /** Set a label override for a specific URL segment */
  setOverride: (segment: string, label: string) => void;
}

const BreadcrumbCtx = createContext<BreadcrumbContextValue>({
  overrides: {},
  setOverride: () => {},
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const setOverride = useCallback((segment: string, label: string) => {
    setOverrides((prev) => {
      if (prev[segment] === label) return prev;
      return { ...prev, [segment]: label };
    });
  }, []);

  const value = useMemo(() => ({ overrides, setOverride }), [overrides, setOverride]);

  return <BreadcrumbCtx.Provider value={value}>{children}</BreadcrumbCtx.Provider>;
}

export function useBreadcrumbOverrides() {
  return useContext(BreadcrumbCtx);
}
